import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import { useLanguage } from '../../i18n/LanguageContext';
import api from '../../services/api';

interface ReceiptLine {
  purchaseOrderLineId: number;
  productId: number;
  productName: string;
  ordered: number;
  alreadyReceived: number;
  remaining: number;
  quantity: number;
}

const GoodsReceiptForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [error, setError] = useState('');
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [lines, setLines] = useState<ReceiptLine[]>([]);
  const [form, setForm] = useState({
    purchaseOrderId: '' as any,
    receiptDate: new Date().toISOString().split('T')[0],
    warehouseId: '' as any,
    notes: ''
  });

  useEffect(() => {
    loadPurchaseOrders();
    loadWarehouses();
  }, []);

  const loadPurchaseOrders = async () => {
    try {
      const res = await procurementService.getPurchaseOrders();
      // Only show APPROVED or ORDERED POs that can receive goods
      setPurchaseOrders(res.data.filter((po: any) =>
        po.status === 'APPROVED' || po.status === 'ORDERED'
      ));
    } catch (err) { console.error(err); }
  };

  const loadWarehouses = async () => {
    try {
      const res = await api.get('/categoriesdepartments/categories');
      // Fallback: load warehouses from a dedicated endpoint or use hardcoded
      setWarehouses([
        { id: 1, name: 'Main Warehouse (WH-MAIN)' },
        { id: 2, name: 'Secondary Warehouse (WH-SEC)' }
      ]);
    } catch (err) { console.error(err); }
  };

  const handlePOChange = async (poId: number) => {
    setForm({ ...form, purchaseOrderId: poId });
    try {
      const res = await procurementService.getPurchaseOrder(poId);
      if (res.data.lines) {
        setLines(res.data.lines.map((l: any) => ({
          purchaseOrderLineId: l.id,
          productId: l.productId,
          productName: l.productName,
          ordered: l.quantity,
          alreadyReceived: l.receivedQuantity,
          remaining: l.quantity - l.receivedQuantity,
          quantity: l.quantity - l.receivedQuantity // Default to remaining
        })));
      }
    } catch (err) { console.error(err); }
  };

  const updateQuantity = (index: number, qty: number) => {
    const updated = [...lines];
    updated[index].quantity = Math.min(qty, updated[index].remaining);
    setLines(updated);
  };

  const totalReceiving = lines.reduce((sum, l) => sum + l.quantity, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validLines = lines.filter(l => l.quantity > 0);
    if (validLines.length === 0) {
      setError('At least one line with quantity > 0 is required');
      return;
    }

    if (!form.warehouseId) {
      setError('Please select a warehouse');
      return;
    }

    try {
      await procurementService.createGoodsReceipt({
        purchaseOrderId: form.purchaseOrderId,
        receiptDate: form.receiptDate,
        warehouseId: form.warehouseId,
        notes: form.notes,
        lines: validLines.map(l => ({
          purchaseOrderLineId: l.purchaseOrderLineId,
          productId: l.productId,
          quantity: l.quantity
        }))
      });
      navigate('/goods-receipts');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating goods receipt');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">New Goods Receipt</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/goods-receipts')}>
          Back to List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required select label="Purchase Order" value={form.purchaseOrderId}
          onChange={e => handlePOChange(Number(e.target.value))}>
          <MenuItem value="">Select Purchase Order</MenuItem>
          {purchaseOrders.map((po: any) => (
            <MenuItem key={po.id} value={po.id}>
              {po.orderNumber} — {po.supplierName} (${po.totalAmount?.toFixed(2)})
            </MenuItem>
          ))}
        </TextField>
        <TextField fullWidth required type="date" label="Receipt Date" value={form.receiptDate}
          onChange={e => setForm({ ...form, receiptDate: e.target.value })}
          InputLabelProps={{ shrink: true }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField fullWidth required select label="Warehouse" value={form.warehouseId}
          onChange={e => setForm({ ...form, warehouseId: e.target.value })}>
          <MenuItem value="">Select Warehouse</MenuItem>
          {warehouses.map((w: any) => (
            <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
          ))}
        </TextField>
        <TextField fullWidth label="Notes" value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Box>

      {lines.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>Items to Receive</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Ordered</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Already Received</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Remaining</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="center">Receive Now</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => (
                  <TableRow key={idx} sx={{ bgcolor: line.remaining === 0 ? '#e8f5e9' : 'inherit' }}>
                    <TableCell>{line.productName}</TableCell>
                    <TableCell align="center">{line.ordered}</TableCell>
                    <TableCell align="center">{line.alreadyReceived}</TableCell>
                    <TableCell align="center">{line.remaining}</TableCell>
                    <TableCell>
                      <TextField fullWidth size="small" type="number" value={line.quantity}
                        onChange={e => updateQuantity(idx, Number(e.target.value))}
                        inputProps={{ min: 0, max: line.remaining }}
                        disabled={line.remaining === 0} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }} align="right">
                    Total Receiving:
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {totalReceiving}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large"
          disabled={lines.length === 0 || totalReceiving === 0}>
          Create Goods Receipt
        </Button>
      </Box>
    </Box>
  );
};

export default GoodsReceiptForm;
